// @tennis-matches/services/masterdata/locations
// Shared club locations (Orte/Anlagen) across apps.

import { getSupabaseClient } from "../supabase/index.js";
import { resolveTenantScopeLabelsRemote } from "../governance/tenant-scope-labels.service.js";

/** Sichtbar in globaler Stammdaten-Verwaltung (Spieler, Admins, …). */
export const CLUB_LOCATION_MASTERDATA_SCOPE_SHARED = "shared";

/** Nur TrainingsDesk-Planung im Tenant (nicht in globaler Stammdaten-UI). */
export const CLUB_LOCATION_MASTERDATA_SCOPE_COACH_DESK = "coach_desk";

function __asStr(v) {
  return String(v ?? "").trim();
}

const DEPRECATED_FIELDS = [
  "club_name",
  "access_code",
  "environment",
  "address",
];

function __stripDeprecated(obj) {
  if (!obj || typeof obj !== "object") return obj;
  const cleaned = { ...obj };
  for (const key of DEPRECATED_FIELDS) delete cleaned[key];
  return cleaned;
}

function __normalizeClubLocationRow(row) {
  if (!row || typeof row !== "object") return row;
  const isActive = row.is_active;
  const isInactive =
    typeof row.is_inactive === "boolean" ? row.is_inactive : isActive === false;
  return {
    ...row,
    is_inactive: isInactive,
  };
}

export function normalizeClubLocationRow(row) {
  return __normalizeClubLocationRow(row);
}

function __prepareClubLocationWrite(payload) {
  const cleaned = __stripDeprecated(payload);
  if (!cleaned || typeof cleaned !== "object") return cleaned;
  const out = { ...cleaned };
  if (Object.prototype.hasOwnProperty.call(out, "is_inactive")) {
    out.is_active = out.is_inactive !== true;
    delete out.is_inactive;
  }
  return out;
}

/**
 * Vereinsnamen über RPC resolve_tenant_scope_labels (security definer, kein direktes REST auf club_profiles).
 * Returns Map<tenant_id, club_name>. Graceful: returns empty map on any error.
 */
async function __resolveClubNames(tenantIds) {
  const ids = (Array.isArray(tenantIds) ? tenantIds : [])
    .map((id) => String(id ?? "").trim())
    .filter(Boolean);
  if (!ids.length) return new Map();
  try {
    const res = await resolveTenantScopeLabelsRemote(ids);
    if (!res?.ok || !res.byScopeId) return new Map();
    const out = new Map();
    for (const id of ids) {
      const row = res.byScopeId[id] || res.byScopeId[id.toLowerCase()];
      const name = String(row?.display_name ?? "").trim();
      if (name) out.set(id, name);
    }
    return out;
  } catch (_) {
    return new Map();
  }
}

export function isSharedMasterdataLocation(loc) {
  const scope = String(
    loc?.masterdata_scope || CLUB_LOCATION_MASTERDATA_SCOPE_SHARED,
  ).trim();
  return scope !== CLUB_LOCATION_MASTERDATA_SCOPE_COACH_DESK;
}

export async function loadClubLocations() {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { data, error } = await supabase
      .from("club_locations")
      .select(
        "id,tenant_id,name,location_type,street,postal_code,city,notes,is_active,masterdata_scope,court_fee_eur,guest_fee_eur,created_at,updated_at",
      )
      .order("name", { ascending: true });
    if (error) return { ok: false, error };
    const locations = (data || []).map(__normalizeClubLocationRow);

    const tenantIds = [
      ...new Set(locations.map((l) => l.tenant_id).filter(Boolean)),
    ];
    const profileNames = await __resolveClubNames(tenantIds);

    const enriched = locations.map((l) => {
      const tid = __asStr(l.tenant_id);
      return {
        ...l,
        resolved_club_name: (tid && profileNames.get(tid)) || null,
      };
    });

    return { ok: true, data: enriched };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function createClubLocation(location) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { data, error } = await supabase
      .from("club_locations")
      .insert([__prepareClubLocationWrite(location)])
      .select()
      .single();
    if (error) return { ok: false, error };
    return { ok: true, data: __normalizeClubLocationRow(data) };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function updateClubLocation(id, updates) {
  const lid = __asStr(id);
  if (!lid) return { ok: false, error: "missing-id" };
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { data, error } = await supabase
      .from("club_locations")
      .update(__prepareClubLocationWrite(updates))
      .eq("id", lid)
      .select()
      .single();
    if (error) return { ok: false, error };
    return { ok: true, data: __normalizeClubLocationRow(data) };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function deleteClubLocation(id) {
  const lid = __asStr(id);
  if (!lid) return { ok: false, error: "missing-id" };
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { error } = await supabase
      .from("club_locations")
      .delete()
      .eq("id", lid);
    if (error) return { ok: false, error };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e };
  }
}
